import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, Label, Input } from "reactstrap";

const URL_CARGOS = process.env.REACT_APP_URL_CARGOS_CONTENEDOR;
const URL_CONCEPTOS = process.env.REACT_APP_URL_CONCEPTOS_GASTOS;
const URL_TC = process.env.REACT_APP_URL_TIPOS_CAMBIO;
const URL_LOGS = process.env.REACT_APP_URL_LOGS;

export const TIPOS_CARGO = [
  { value: "Liberacion", label: "Liberación" },
  { value: "Flete", label: "Flete" },
];

const auth = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });

/**
 * Captura o edita un cargo a contenedor. Suma al saldo del cliente y se paga desde Abonos.
 *  props: isOpen, toggle, user, contenedor {_id, referencia, numeroReferencia, idClientes, cliente},
 *         cargo (fila a editar; sin él es alta), onSaved
 */
export default function CargoContenedorModal({ isOpen, toggle, user, contenedor, cargo, onSaved }) {
  const [conceptos, setConceptos] = useState([]);
  const [fecha, setFecha] = useState("");
  const [tipo, setTipo] = useState("");
  const [concepto, setConcepto] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [moneda, setMoneda] = useState("MXN");
  const [totalUSD, setTotalUSD] = useState("");
  const [tipoCambio, setTipoCambio] = useState("");
  const [totalMXN, setTotalMXN] = useState("");
  const [tcDia, setTcDia] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    axios.get(URL_CONCEPTOS, auth()).then(({ data }) => setConceptos(data)).catch(() => setConceptos([]));
    axios.get(`${URL_TC}Vigente`, auth()).then(({ data }) => setTcDia((data && data.tipoCambio) || "")).catch(() => setTcDia(""));
    setFecha(cargo ? cargo.fecha : moment().format("YYYY-MM-DD"));
    setTipo(cargo ? cargo.tipo : "");
    setConcepto(cargo ? cargo.idConcepto : "");
    setDescripcion(cargo ? cargo.descripcion : "");
    setMoneda(cargo ? cargo.moneda : "MXN");
    setTotalUSD(cargo && cargo.moneda === "USD" ? cargo.totalUSD : "");
    setTipoCambio(cargo && cargo.moneda === "USD" ? cargo.tipoCambio : "");
    setTotalMXN(cargo ? cargo.total : "");
  }, [isOpen, cargo]);

  const total = moneda === "USD" ? (Number(totalUSD) || 0) * (Number(tipoCambio) || 0) : Number(totalMXN) || 0;

  const cambiaMoneda = (m) => {
    setMoneda(m);
    if (m === "USD" && !tipoCambio) setTipoCambio(tcDia);
  };

  const guardar = async () => {
    if (!tipo || !concepto || !descripcion.trim() || !fecha || !(total > 0)) {
      Swal.fire({ icon: "warning", title: "Faltan datos", text: "Captura fecha, tipo, concepto, descripción e importe" });
      return;
    }
    setGuardando(true);
    const datos = {
      tipo,
      fecha,
      conceptosGastos: concepto,
      descripcion: descripcion.trim(),
      moneda,
      totalUSD: moneda === "USD" ? Number(totalUSD) : 0,
      tipoCambio: moneda === "USD" ? Number(tipoCambio) : 1,
      total: Math.round(total * 100) / 100,
    };
    try {
      if (cargo) {
        await axios.patch(`${URL_CARGOS}EditImporte/${cargo._id}`, datos, auth());
      } else {
        await axios.post(URL_CARGOS, { ...datos, contenedores: contenedor._id, clientes: contenedor.idClientes }, auth());
      }
      axios.post(
        URL_LOGS,
        { tipo: cargo ? "Editar Cargo" : "Crear Cargo", detalle: `${contenedor.referencia} / ${descripcion} / ${datos.total}`, user: user.id },
        auth()
      );
      Swal.fire({ icon: "success", title: "Cargo guardado", timer: 1300, showConfirmButton: false });
      toggle();
      if (onSaved) onSaved();
    } catch (err) {
      Swal.fire({ icon: "error", title: "No se pudo guardar", text: err?.response?.data?.message || err.message });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <span>{cargo ? "Editar cargo" : "Cargo"} · Contenedor {contenedor ? contenedor.referencia : ""}</span>
      </ModalHeader>
      <ModalBody>
        <Row>
          <Col md={4} className="mb-2">
            <Label>Fecha</Label>
            <Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </Col>
          <Col md={8} className="mb-2">
            <Label>Cliente</Label>
            <Input type="text" value={contenedor ? contenedor.cliente : ""} disabled />
          </Col>
          <Col md={4} className="mb-2">
            <Label>Tipo de cargo</Label>
            <Input type="select" value={tipo} onChange={(e) => setTipo(e.target.value)}>
              <option value="">Selecciona</option>
              {TIPOS_CARGO.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </Input>
          </Col>
          <Col md={4} className="mb-2">
            <Label>Concepto</Label>
            <Input type="select" value={concepto} onChange={(e) => setConcepto(e.target.value)}>
              <option value="">Selecciona</option>
              {[...conceptos].sort((a, b) => (a.name > b.name ? 1 : -1)).map((a) => (
                <option key={a._id} value={a._id}>{a.name}</option>
              ))}
            </Input>
          </Col>
          <Col md={4} className="mb-2">
            <Label>Descripción</Label>
            <Input type="text" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
          </Col>
          <Col md={3} className="mb-2">
            <Label>Moneda</Label>
            <Input type="select" value={moneda} onChange={(e) => cambiaMoneda(e.target.value)}>
              <option value="MXN">MXN</option>
              <option value="USD">USD</option>
            </Input>
          </Col>
          {moneda === "USD" ? (
            <>
              <Col md={3} className="mb-2">
                <Label>Total USD</Label>
                <Input type="number" min="0" step="any" value={totalUSD} onChange={(e) => setTotalUSD(e.target.value)} />
              </Col>
              <Col md={3} className="mb-2">
                <Label>Tipo de cambio</Label>
                <Input type="number" min="0" step="any" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} />
              </Col>
              <Col md={3} className="mb-2">
                <Label>Total MXN</Label>
                <Input type="number" value={Math.round(total * 100) / 100} disabled />
              </Col>
            </>
          ) : (
            <Col md={3} className="mb-2">
              <Label>Total MXN</Label>
              <Input type="number" min="0" step="any" value={totalMXN} onChange={(e) => setTotalMXN(e.target.value)} />
            </Col>
          )}
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}><i className="fas fa-times" /> Cerrar</Button>
        <Button color="success" onClick={guardar} disabled={guardando}><i className="fas fa-save" /> Guardar</Button>
      </ModalFooter>
    </Modal>
  );
}
